// cartographer/planOutline — render a MultiSheetWorkflowState as the
// plain-text "SUBSYSTEM → GROUPS → KEY BLOCKS" outline that the AI
// prompt asks the model to produce before emitting JSON (handover §2,
// phase B). The two paths — deterministic upload-zip and manual LLM
// authoring — share this format so users can compare outputs.

import type { MultiSheetWorkflowState } from '@/types/workflow';

export interface PlanOutlineOptions {
  /** Max blocks listed per group before "…+ N more" tail. */
  maxBlocksPerGroup?: number;
  /** Include cross-sheet connectors at the bottom. */
  includeCrossSheet?: boolean;
}

const DEFAULT_OPTS: Required<PlanOutlineOptions> = {
  maxBlocksPerGroup: 5,
  includeCrossSheet: true,
};

/**
 * Render a workflow as a human-readable plan outline. Pure — no I/O.
 *
 * Example output:
 *
 *   PLAN — my-repo
 *
 *   ① Pages (3 groups · 14 blocks)
 *     ├─ src/pages/admin/ (5 files)
 *     │    · Admin.tsx
 *     │    · UsersPanel.tsx
 *     │    · …+ 3 more
 *     ├─ src/pages/auth/ (4 files)
 *     │    · …
 *
 *   ② Components (2 groups · 8 blocks)
 *     ├─ …
 *
 *   Cross-sheet links:
 *     ① → ② via "renders pages"
 */
export function summariseWorkflowAsPlan(
  workflow: MultiSheetWorkflowState,
  options: PlanOutlineOptions = {},
): string {
  const opts = { ...DEFAULT_OPTS, ...options };
  const lines: string[] = [];

  lines.push(`PLAN — ${workflow.name}`);
  lines.push('');

  workflow.sheets.forEach((sheet, sheetIdx) => {
    const numbered = sheet.name.match(/^[①-⑳]/) ? sheet.name : `${sheetIdx + 1}. ${sheet.name}`;
    const totalBlocks = sheet.blocks.length;
    lines.push(`${numbered}  (${sheet.groups.length} groups · ${totalBlocks} blocks)`);

    if (sheet.groups.length === 0 && sheet.blocks.length > 0) {
      // Ungrouped blocks — still show them.
      const sample = sheet.blocks.slice(0, opts.maxBlocksPerGroup);
      for (const b of sample) {
        lines.push(`  · ${b.config.name}`);
      }
      if (sheet.blocks.length > opts.maxBlocksPerGroup) {
        lines.push(`  · …+ ${sheet.blocks.length - opts.maxBlocksPerGroup} more`);
      }
    }

    for (const group of sheet.groups) {
      const memberBlocks = group.blockIds
        .map((id) => sheet.blocks.find((b) => b.id === id))
        .filter((b): b is NonNullable<typeof b> => b !== undefined);
      lines.push(`  ├─ ${group.name}`);
      const sample = memberBlocks.slice(0, opts.maxBlocksPerGroup);
      for (const b of sample) {
        lines.push(`  │    · ${b.config.name}`);
      }
      if (memberBlocks.length > opts.maxBlocksPerGroup) {
        lines.push(`  │    · …+ ${memberBlocks.length - opts.maxBlocksPerGroup} more`);
      }
    }
    lines.push('');
  });

  if (opts.includeCrossSheet && workflow.crossSheetConnectors.length > 0) {
    lines.push('Cross-sheet links:');
    for (const xs of workflow.crossSheetConnectors) {
      const fromName = workflow.sheets[xs.fromSheetIndex]?.name ?? `sheet ${xs.fromSheetIndex}`;
      const toName = workflow.sheets[xs.toSheetIndex]?.name ?? `sheet ${xs.toSheetIndex}`;
      const labelSuffix = '';
      lines.push(`  ${fromName} → ${toName}${labelSuffix}`);
    }
  }

  return lines.join('\n').trimEnd();
}
